/* =========================================================
   ¿Se puede editar ahora mismo?

   Una sola respuesta para toda la aplicación, porque hasta ahora no
   había ninguna: el botón «Editar» estaba siempre encendido. Con la
   ventana cerrada, un socio entraba en modo edición, rellenaba la ficha
   entera y al guardar el servidor la rechazaba —correctamente— pero el
   trabajo ya estaba hecho y se perdía.

   El servidor sigue siendo quien manda: esto no sustituye su
   comprobación, solo evita ofrecer una puerta que está cerrada.

   Las reglas son las mismas que aplica `writeAllowed` en el servidor:
     · el Admin ETC escribe siempre, con la ventana abierta o cerrada;
     · el gerente de site no escribe nunca, es un rol de solo lectura;
     · el admin de site escribe su site y sus instructores; la ficha de su
       organización solo si se la concedieron de verdad — writableOrgIds no
       deriva la organización dueña del centro, y el servidor manda esa
       respuesta con la sesión como `puedeEditarOrg`;
     · los demás, solo mientras la ventana esté abierta.
   ========================================================= */
import { useAppStore } from '@/store/useAppStore';

export interface PermisoEdicion {
  puede: boolean;
  /** Por qué no, para poder decirlo en vez de dejar un botón muerto. */
  motivo: string;
  /** El «no» es del rol, no del momento: ni esperando a que abra la ventana
   *  esta cuenta va a poder. La pantalla enseña entonces un distintivo de
   *  solo lectura, no un botón apagado que invite a volver más tarde. */
  permanente: boolean;
}

/** Qué ficha se quiere tocar. La organización tiene una regla propia. */
export type Ambito = 'org' | 'site' | 'instructor';

export function usePuedeEditar(ambito: Ambito = 'site'): PermisoEdicion {
  const role = useAppStore((s) => s.role);
  const abierta = useAppStore((s) => s.windowState.open);
  const lang = useAppStore((s) => s.lang);
  const user = useAppStore((s) => s.user);
  const es = lang !== 'en';

  if (role === 'ETC_ADMIN') return { puede: true, motivo: '', permanente: false };

  if (role === 'SITE_MGR') {
    return {
      puede: false,
      motivo: es
        ? 'Tu cuenta es de solo consulta. Para cambiar datos hace falta un rol con permiso de edición.'
        : 'Your account is read-only. Changing data requires an editing role.',
      permanente: true,
    };
  }

  // `undefined` es la demostración, que no manda el dato: allí manda el rol.
  const orgConcedida = user?.puedeEditarOrg ?? role !== 'SITE_ADMIN';
  if (ambito === 'org' && !orgConcedida) {
    return {
      puede: false,
      motivo: es
        ? 'Tu cuenta administra un site. Los datos de la organización los cambia el gerente del socio.'
        : 'Your account administers a site. Organization data is changed by the partner manager.',
      permanente: true,
    };
  }

  if (!abierta) {
    return {
      puede: false,
      motivo: es
        ? 'El proceso de actualización está cerrado. Mientras esté cerrado, el portal es de solo lectura para los socios.'
        : 'The update process is closed. While it is closed the portal is read-only for partners.',
      permanente: false,
    };
  }

  return { puede: true, motivo: '', permanente: false };
}
